import React, { useState, useEffect } from 'react';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import Search from 'lucide-react/dist/esm/icons/search';
import Database from 'lucide-react/dist/esm/icons/database';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import Brain from 'lucide-react/dist/esm/icons/brain';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import FolderOpen from 'lucide-react/dist/esm/icons/folder-open';
import { getTranslation } from '../../i18n';
import type { SupportedLanguage } from '../../types';
import type { LucideIcon } from 'lucide-react';

interface Stage {
  message: string;
  icon: LucideIcon;
}

const ANALYSIS_STAGES: Stage[] = [
  { message: 'Extracting text from document...', icon: FileText },
  { message: 'Identifying financial statements...', icon: Search },
  { message: 'Parsing revenue and cost data...', icon: Database },
  { message: 'Analyzing profitability metrics...', icon: TrendingUp },
  { message: 'Detecting anomalies and patterns...', icon: AlertTriangle },
  { message: 'Running forensic analysis algorithms...', icon: Brain },
  { message: 'Generating AI insights...', icon: Sparkles },
];

const OPENING_STAGES: Stage[] = [
  { message: 'Loading analysis data...', icon: FolderOpen },
  { message: 'Preparing results...', icon: FileText },
];

interface LoadingSpinnerProps {
  language?: SupportedLanguage;
  mode?: 'analyzing' | 'opening';
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ language = 'en', mode = 'analyzing' }) => {
  const t = (key: string) => getTranslation(language, key);
  const [stageIndex, setStageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const isOpening = mode === 'opening';
  const stages = isOpening ? OPENING_STAGES : ANALYSIS_STAGES;
  const intervalTime = isOpening ? 2000 : 4000;

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setStageIndex(prev => (prev + 1) % stages.length);
        setIsTransitioning(false);
      }, 300);
    }, intervalTime);

    return () => clearInterval(interval);
  }, [stages.length, intervalTime]);

  useEffect(() => {
    setStageIndex(0);
  }, [mode]);

  const currentStage = stages[stageIndex];
  const StageIcon = currentStage.icon;
  
  const title = isOpening ? 'OPENING ANALYSIS' : t('analyzing');
  const subtitle = isOpening ? 'Loading your saved analysis' : t('analyzingSubtitle');
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[500px] p-12 animate-fade-in">
      <div className="relative flex flex-col items-center p-16 rounded-3xl glass">
        {/* Spinner */}
        <div className="relative w-32 h-32 mb-10">
          {/* Rotating glows */}
          <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(33,158,188,0.4)_0%,transparent_70%)] animate-spin" style={{ animationDuration: '3s' }} />
          <div className="absolute inset-[-10px] rounded-full bg-[radial-gradient(circle,rgba(16,185,129,0.3)_0%,transparent_70%)] animate-spin" style={{ animationDuration: '4s', animationDirection: 'reverse' }} />
          
          {/* Rings */}
          <div className="absolute inset-0 border-4 border-default rounded-full" />
          <div className="absolute inset-2 border-4 border-brand/30 rounded-full animate-spin" style={{ animationDuration: '2s', borderTopColor: '#219EBC' }} />
          
          {/* Logo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <img src="/quazi-glyph.svg" alt="QUAZI" className="w-14 h-14 drop-shadow-[0_0_20px_rgba(33,158,188,0.8)] animate-pulse" />
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-content mb-2 tracking-[0.15em] uppercase">{title}</h2>
        <p className="text-sm text-content-tertiary mb-8">{subtitle}</p>
        
        {/* Stage indicator */}
        <div className={`flex items-center gap-3 py-3 px-6 bg-brand/10 border border-brand/30 rounded-full transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
          <StageIcon size={18} className="text-primary" />
          <span className="text-sm text-primary font-medium">{currentStage.message}</span>
        </div>
      </div>
    </div>
  );
};

export default LoadingSpinner;
